import { createEnum, getUserName } from "./utils";
import { ReactComponent as Epic } from "assets/Icons/Epic.svg";
import { ReactComponent as Story } from "assets/Icons/Story.svg";
import { ReactComponent as Task } from "assets/Icons/Task.svg";
import { ReactComponent as SubTask } from "assets/Icons/Subtask.svg";
import { ReactComponent as Bug } from "assets/Icons/Bug.svg";
import { ReactComponent as Highest } from "assets/Icons/Highest.svg";
import { ReactComponent as High } from "assets/Icons/High.svg";
import { ReactComponent as Low } from "assets/Icons/Low.svg";
import { ReactComponent as Lowest } from "assets/Icons/Lowest.svg";
import { PATH } from "./pagePath";
import Email from "assets/Icons/Email";
import Support from "assets/Icons/Support";
import { SUPPORT_WORK_TYPE_COLORS } from "./supportWorkTypeColors";

export const COOKIES = {
  ACCESS_TOKEN: "accessToken",
  REFERESH_TOKEN: "refreshToken",
  USER_ID: "userid",
  PERMISSIONS: "permissions",
  ROLE: "role",
};

export const DEFAULT_CREATE_ISSUE_TYPE = "STORY";

export const issueTypeIcons = {
  EPIC: <Epic height={"20px"} width={"20px"} />,
  STORY: <Story height={"20px"} width={"20px"} />,
  TASK: <Task height={"20px"} width={"20px"} />,
  SUBTASK: <SubTask height={"18px"} width={"18px"} />,
  BUG: <Bug height={"20px"} width={"20px"} />,
};

export { SUPPORT_WORK_TYPE_COLORS };

export const workTypeIcons ={
  EMAIL: <Email height={"20px"} width={"20px"} />,
  REQUEST: <Support height={"20px"} width={"20px"} />,
};

/** Maps API workType name (e.g. "Emailed request") to workTypeIcons key. */
export const getSupportWorkTypeIconKey = (workType) => {
  const normalizedType = String(workType ?? "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");

  if (Object.prototype.hasOwnProperty.call(workTypeIcons, normalizedType)) {
    return normalizedType;
  }
  if (normalizedType.includes("EMAIL")) return "EMAIL";
  return "REQUEST";
};

/** Border / accent color matching the work-type icon background. */
export const getSupportWorkTypeColor = (workType) => {
  const key = getSupportWorkTypeIconKey(workType);
  return SUPPORT_WORK_TYPE_COLORS[key] ?? SUPPORT_WORK_TYPE_COLORS.REQUEST;
};

export const SUPPORT_WORK_TYPE_BADGE_STYLES = {
  EMAIL: { backgroundColor: "#EBF5FF", color: "#5B9AD4" },
  REQUEST: { backgroundColor: "#FEF8E6", color: "#C9A20A" },
};

/** Badge styles matching the work-type icon (like Story/Bug/Task badges). */
export const getSupportWorkTypeBadgeStyles = (workType) => {
  const key = getSupportWorkTypeIconKey(workType);
  return (
    SUPPORT_WORK_TYPE_BADGE_STYLES[key] ??
    SUPPORT_WORK_TYPE_BADGE_STYLES.REQUEST
  );
};

export const priorityIcons = {
  HIGHEST: <Highest height={"20px"} width={"20px"} />,
  HIGH: <High height={"20px"} width={"20px"} />,
  LOW: <Low height={"20px"} width={"20px"} />,
  LOWEST: <Lowest height={"20px"} width={"20px"} />,
};

export const issueTypeOptions = [
  { optionLabel: "Epic", value: "EPIC", icon: issueTypeIcons["EPIC"] },
  { optionLabel: "Story", value: "STORY", icon: issueTypeIcons["STORY"] },
  { optionLabel: "Task", value: "TASK", icon: issueTypeIcons["TASK"] },
  { optionLabel: "Bug", value: "BUG", icon: issueTypeIcons["BUG"] },
];

export const sprintStatusTypeOptions = [
  { optionLabel: "Start", value: "START"},
  { optionLabel: "Started", value: "STARTED"},
  { optionLabel: "Completed", value: "COMPLETED"},
];

export const statusCategory = [
  { optionLabel: "Todo", value: "TO_DO" },
  { optionLabel: "Inprogress", value: "IN_PROGRESS" },
  { optionLabel: "Done", value: "DONE" },
];

export const issueTypeOptionsActiveSprint = [
  { optionLabel: "Story", value: "STORY", icon: issueTypeIcons["STORY"] },
  { optionLabel: "Task", value: "TASK", icon: issueTypeIcons["TASK"] },
  { optionLabel: "Bug", value: "BUG", icon: issueTypeIcons["BUG"] },
];


export const projectMemberOptions = [
  { optionLabel: getUserName(), value: getUserName() },
];

export const priorityOptions = [
  { optionLabel: "Highest", value: "HIGHEST", icon: priorityIcons["HIGHEST"] },
  { optionLabel: "High", value: "HIGH", icon: priorityIcons["HIGH"] },
  { optionLabel: "Low", value: "LOW", icon: priorityIcons["LOW"] },
  { optionLabel: "Lowest", value: "LOWEST", icon: priorityIcons["LOWEST"] },
];

export const severityOptions = [
  { optionLabel: "Blocker", value: "BLOCKER" },
  { optionLabel: "Critical", value: "CRITICAL" },
  { optionLabel: "Major", value: "MAJOR" },
  { optionLabel: "Minor", value: "MINOR" },
  { optionLabel: "Medium", value: "MEDIUM" },
];

export const templateTypeOptions = [
  { optionLabel: "Scrum", value: "SCRUM" },
  { optionLabel: "Support", value: "SUPPORT" },
];

export const linkedIssuesRelatedToOptions = [
  { optionLabel: "clones", value: "CLONES" },
  { optionLabel: "blocks", value: "BLOCKS" },
  { optionLabel: "relates to", value: "RELATES_TO" },
  { optionLabel: "is blocked by", value: "IS_BLOCKED_BY" },
  { optionLabel: "is cloned by", value: "IS_CLONED_BY" },
  { optionLabel: "duplicates", value: "DUPLICATES" },
  { optionLabel: "is duplicated by", value: "IS_DUPLICATED_BY" },
  { optionLabel: "is idea for", value: "IS_IDEA_FOR" },
  { optionLabel: "added to idea", value: "ADDED_TO_IDEA" },
  { optionLabel: "implements", value: "IMPLEMENTS" },
  { optionLabel: "is implemented by", value: "IS_IMPLEMENTED_BY" },
  { optionLabel: "merged from", value: "MERGED_FROM" },
  { optionLabel: "reviews", value: "REVIEWS" },
  { optionLabel: "is reviewed by", value: "IS_REVIEWED_BY" },
  { optionLabel: "causes", value: "CAUSES" },
  { optionLabel: "is caused by", value: "IS_CAUSED_BY" },
];

export const supportTypeOptions = [
  { optionLabel: "Feature Request", value: "FEATURE_REQUEST" },
  { optionLabel: "Raise Bug", value: "RAISE_BUG" },
  { optionLabel: "Feedback", value: "FEEDBACK" },
]

export const dashboardStatusLabels = {
  "completed": "Completed",
  "dueSoon": "Due Soon",
  "created": "Created",
  "updated": "Updated"
}

export const baseURL = () => {
  return process.env.REACT_APP_API_BASE_URL
}

export const TEMPLATE_TYPE = {
  support : "SUPPORT",
  scrum : "SCRUM",
  kanban : "KANBAN"
}
createEnum(TEMPLATE_TYPE)